import { useMemo, useState } from 'react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import type { StepRow } from '../../../api/outreach';
import { CopyDrawer } from './CopyDrawer';
import { Num, Rate, stepTypeLabel } from './format';

/** Step types that send nothing, so send/reply columns do not apply. */
const NON_SENDING_TYPES = new Set(['conditional']);

/** lemlist's "pending invitation" error: a message fired before the invite was accepted. Not a failure. */
const PENDING_INVITATION = /pending.*invitation/i;

/** Messages held because the invite wasn't accepted yet, out of the step's failed count. */
function beforeAcceptCount(step: StepRow): number {
  const n = step.failureReasons.filter((r) => PENDING_INVITATION.test(r.message)).reduce((sum, r) => sum + r.count, 0);
  return Math.min(n, step.failed);
}

function failureTooltip(step: StepRow): string | undefined {
  const lines = step.failureReasons.map((r) => `${r.count}× ${r.message}`);
  if (step.skipped > 0) lines.push(`${step.skipped} skipped`);
  return lines.length > 0 ? lines.join('\n') : undefined;
}

/**
 * The "which messages" table: the campaign's sequence tree in lemlist's index
 * order, branch steps indented under their condition. Replies, interested and
 * not-interested are counted on the step that provoked them.
 */
export function StepsTable({ steps }: { steps: StepRow[] }) {
  const [open, setOpen] = useState<StepRow | null>(null);
  const ordered = useMemo(() => [...steps].sort((a, b) => a.index - b.index), [steps]);
  // Reaction columns that read zero on every step say nothing; they come back once a lead is marked.
  const showInterested = useMemo(() => steps.some((s) => s.interested > 0), [steps]);
  const showNotInterested = useMemo(() => steps.some((s) => s.notInterested > 0), [steps]);

  return (
    <Card padding="md">
      <CardHeader
        title="Sequence steps"
        description="Sent counts distinct leads per step; reply % is replies ÷ sent on that step. Hover Failed for the reasons, click a row for the copy."
      />
      {ordered.length === 0 ? (
        <p className="py-6 text-center text-body-sm text-text-tertiary">
          No sequence steps synced for this campaign yet. They arrive with the next lemlist sync.
        </p>
      ) : (
        // The last column changes with the reaction columns, so its right padding is set on whichever comes last.
        <Table minWidth={1000} className="[&_tr>*:last-child]:!pr-3">
            <THead>
              <tr>
                <Th>Step</Th>
                <Th>Type</Th>
                <Th align="right">Delay</Th>
                <Th align="right">Sent</Th>
                <Th align="right">Failed</Th>
                <Th align="right">Opened</Th>
                <Th align="right">Accepted</Th>
                <Th align="right">Replies</Th>
                <Th align="right">Reply %</Th>
                {showInterested && <Th align="right">Interested</Th>}
                {showNotInterested && <Th align="right">Not interested</Th>}
              </tr>
            </THead>
            <tbody>
              {ordered.map((step) => {
                const sends = !NON_SENDING_TYPES.has(step.type);
                const tooltip = failureTooltip(step);
                const beforeAccept = beforeAcceptCount(step);
                const realFailed = step.failed - beforeAccept;
                return (
                  <Tr key={step.stepId} onClick={() => setOpen(step)}>
                    <Td>
                      <div style={{ paddingLeft: `${Math.max(0, step.level) * 20}px` }} className="flex flex-col">
                        <span className="tabular-nums font-medium text-text-primary">
                          {step.level > 0 && <span className="mr-1 text-text-tertiary">↳</span>}
                          {step.index}
                        </span>
                        {step.branchLabel && (
                          <span className="text-body-xs text-text-tertiary">
                            {step.branchLabel}
                            {step.branchTaken !== null && (
                              <span className="tabular-nums"> · {step.branchTaken} routed</span>
                            )}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="text-text-secondary">{stepTypeLabel(step.type)}</Td>
                    <Td align="right" className="tabular-nums text-text-tertiary">{step.delayDays}d</Td>
                    <Td align="right">{sends ? <Num value={step.sent} strong /> : <Num value={null} />}</Td>
                    <Td align="right">
                      {step.failed + step.skipped > 0 ? (
                        <span className="tabular-nums cursor-help underline decoration-dotted" title={tooltip}>
                          {/* Messages sent before the invite was accepted are a sequence-design issue, not failures: grey, in the banner's words. */}
                          {realFailed > 0 && <span className="text-error-dark">{realFailed}</span>}
                          {beforeAccept > 0 && (
                            <span className="block text-body-xs text-text-tertiary">{beforeAccept} sent before accept</span>
                          )}
                          {step.skipped > 0 && <span className="ml-1 text-body-xs text-text-tertiary">+{step.skipped} skipped</span>}
                        </span>
                      ) : (
                        <Num value={sends ? 0 : null} />
                      )}
                    </Td>
                    <Td align="right">{sends ? <Num value={step.opened} /> : <Num value={null} />}</Td>
                    <Td align="right">
                      {step.type === 'linkedinInvite' || step.accepted > 0 ? <Num value={step.accepted} /> : <Num value={null} />}
                    </Td>
                    <Td align="right">{sends ? <Num value={step.replies} /> : <Num value={null} />}</Td>
                    <Td align="right">
                      {sends ? <Rate value={step.replyRate} n={step.replies} total={step.sent} /> : <Num value={null} />}
                    </Td>
                    {showInterested && <Td align="right">{sends ? <Num value={step.interested} /> : <Num value={null} />}</Td>}
                    {showNotInterested && <Td align="right">{sends ? <Num value={step.notInterested} /> : <Num value={null} />}</Td>}
                  </Tr>
                );
              })}
            </tbody>
        </Table>
      )}
      <CopyDrawer step={open} onClose={() => setOpen(null)} />
    </Card>
  );
}
